import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { Escalation } from '@/types/unlock'

type DealAdvisorSubTabsType = {
  notes: string
  notifications: string
  messages: string
  escalations: string
  alerts: string
}

type StateType = {
  pullCreditModalOpen: boolean
  pullCreditModalAction: string
  pullCreditModalUser: string
  viewTradelinesModalOpen: boolean
  titleOrderModalOpen: boolean | number
  titleOrderDrawerOpen: boolean
  avmReportModalOpen: boolean
  appraisalOrderModalOpen: boolean | number
  username?: string
  dealAdvisorTab: string
  dealAdvisorOpen: boolean
  dealAdvisorSubTabs: DealAdvisorSubTabsType
  dealAdvisorNotesSubTab: 'deal' | 'streaming'
  fileContactsSubTab: 'all' | 'applicants' | 'deal-team' | 'third-party'
  streamingNoteValue: string
  streamingNoteValueReply: string
  contactsCardsStatus: 'custom' | 'allExpanded' | 'allCollapsed'
  hideSystemMessages: boolean
  showWorkviewsFilters: boolean
  manuallyCreateDealOpen: boolean
  createTradelinesModalOpen: boolean
  docDrawModalOpen: boolean
  addContactDrawerOpen: boolean
  closedLossDrawerOpen: boolean
  closedLossDrawerMode: 'uam-decline' | 'closed-loss'
  UAMDeclineModalEscalation: Escalation | null
  closedLossDrawerType: string | null
  closedLossDrawerReason: string | null
  closedLossDrawerSubReasons: { value: string; label: string }[] | null
  closedLossDrawerSubReasonOtherText: string
  pullCreditDrawerStep: number
  aanReasons: string[]
  avmDrawerStep: number
  escalationApprovalDrawerOpen: boolean
  escalationApprovalDrawerEscalation?: Escalation
  alertsTypes?: string[]
  addEscalationDrawerOpen: boolean
  currentStreamingNotePage: number
  manualApplicationDrawerOpen: boolean
  manualApplicationDrawerStep: number
  addTradelineDrawerOpen: boolean
}

const initialState: StateType = {
  pullCreditModalOpen: false,
  pullCreditModalAction: '',
  pullCreditModalUser: '',
  viewTradelinesModalOpen: false,
  titleOrderModalOpen: false,
  titleOrderDrawerOpen: false,
  avmReportModalOpen: false,
  appraisalOrderModalOpen: false,
  dealAdvisorOpen: false,
  dealAdvisorTab: 'notes',
  dealAdvisorSubTabs: {
    notes: 'streaming',
    notifications: 'all',
    messages: 'all',
    escalations: 'exceptions',
    alerts: 'all',
  },
  dealAdvisorNotesSubTab: 'streaming',
  fileContactsSubTab: 'all',
  streamingNoteValue: '',
  streamingNoteValueReply: '',
  contactsCardsStatus: 'custom',
  hideSystemMessages: false,
  showWorkviewsFilters: true,
  manuallyCreateDealOpen: false,
  createTradelinesModalOpen: false,
  docDrawModalOpen: false,
  addContactDrawerOpen: false,
  closedLossDrawerMode: 'closed-loss',
  UAMDeclineModalEscalation: null,
  closedLossDrawerOpen: false,
  closedLossDrawerReason: '',
  closedLossDrawerSubReasons: [],
  closedLossDrawerSubReasonOtherText: '',
  closedLossDrawerType: '',
  pullCreditDrawerStep: 0,
  aanReasons: [],
  avmDrawerStep: 0,
  escalationApprovalDrawerOpen: false,
  escalationApprovalDrawerEscalation: null,
  addEscalationDrawerOpen: false,
  currentStreamingNotePage: 1,
  manualApplicationDrawerOpen: false,
  manualApplicationDrawerStep: 0,
  addTradelineDrawerOpen: false,
}

const generalSlice = createSlice({
  name: 'general',
  initialState,
  reducers: {
    setPullCreditModalOpen: (state, action: PayloadAction<boolean>) => {
      state.pullCreditModalOpen = action.payload
    },
    setPullCreditModalUser: (state, action: PayloadAction<string>) => {
      state.pullCreditModalUser = action.payload
    },
    setPullCreditModalAction: (state, action: PayloadAction<string>) => {
      state.pullCreditModalAction = action.payload
    },
    setPullCreditDrawerStep: (state, action: PayloadAction<number>) => {
      state.pullCreditDrawerStep = action.payload
    },
    setAvmDrawerStep: (state, action: PayloadAction<number>) => {
      state.avmDrawerStep = action.payload
    },
    setAanReasons: (state, action: PayloadAction<string[]>) => {
      state.aanReasons = action.payload
    },
    setViewTradelinesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.viewTradelinesModalOpen = action.payload
    },
    setCreateTradelinesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.createTradelinesModalOpen = action.payload
    },
    setTitleOrderModalOpen: (state, action: PayloadAction<boolean | number>) => {
      state.titleOrderModalOpen = action.payload
    },
    setTitleOrderDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.titleOrderDrawerOpen = action.payload
    },
    setAvmReportModalOpen: (state, action: PayloadAction<boolean>) => {
      state.avmReportModalOpen = action.payload
    },
    setAddContactDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.addContactDrawerOpen = action.payload
    },
    setAddEscalationDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.addEscalationDrawerOpen = action.payload
    },
    setClosedLossDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.closedLossDrawerOpen = action.payload
    },
    setClosedLossDrawerMode: (state, action: PayloadAction<'uam-decline' | 'closed-loss'>) => {
      state.closedLossDrawerMode = action.payload
    },
    setUAMDeclineModalOpen: (state, action: PayloadAction<boolean>) => {
      state.closedLossDrawerMode = action.payload ? 'uam-decline' : 'closed-loss'
      state.UAMDeclineModalEscalation = !action.payload ? null : state.UAMDeclineModalEscalation
      state.closedLossDrawerOpen = action.payload
    },
    setUAMDeclineEscalation: (state, action: PayloadAction<Escalation>) => {
      state.UAMDeclineModalEscalation = action.payload
    },
    setAppraisalOrderModalOpen: (state, action: PayloadAction<boolean | number>) => {
      state.appraisalOrderModalOpen = action.payload
    },
    setClosedLossDrawerType: (state, action: PayloadAction<string | null>) => {
      state.closedLossDrawerType = action.payload
    },
    setClosedLossDrawerReason: (state, action: PayloadAction<string | null>) => {
      state.closedLossDrawerReason = action.payload
    },
    setClosedLossDrawerSubReasons: (
      state,
      action: PayloadAction<{ value: string; label: string }[] | null>
    ) => {
      state.closedLossDrawerSubReasons = action.payload
    },
    setClosedLossDrawerSubReasonOtherText: (state, action: PayloadAction<string | null>) => {
      state.closedLossDrawerSubReasonOtherText = action.payload
    },
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload
    },
    setDealAdvisorOpen: (state, action: PayloadAction<boolean>) => {
      state.dealAdvisorOpen = action.payload
    },
    setDealAdvisorTab: (state, action: PayloadAction<string>) => {
      state.dealAdvisorTab = action.payload
    },
    setDealAdvisorNotesSubTab: (state, action: PayloadAction<'streaming' | 'deal'>) => {
      state.dealAdvisorNotesSubTab = action.payload
    },
    setFileContactsSubTab: (
      state,
      action: PayloadAction<'all' | 'applicants' | 'deal-team' | 'third-party'>
    ) => {
      state.fileContactsSubTab = action.payload
    },
    setDealAdvisorSubTab: (
      state,
      action: PayloadAction<{
        tab: keyof DealAdvisorSubTabsType
        value: string
      }>
    ) => {
      const { tab, value } = action.payload

      state.dealAdvisorSubTabs[tab] = value
    },
    setAddStreamingNoteValue: (state, action: PayloadAction<string>) => {
      state.streamingNoteValue = action.payload
    },
    setAddStreamingNoteValueReply: (state, action: PayloadAction<string>) => {
      state.streamingNoteValueReply = action.payload
    },
    hideSystemMessages: (state, action: PayloadAction<boolean>) => {
      state.hideSystemMessages = action.payload
    },
    setShowWorkviewsFilters: (state, action: PayloadAction<boolean>) => {
      state.showWorkviewsFilters = action.payload
    },
    setManuallyCreateDealOpen: (state, action: PayloadAction<boolean>) => {
      state.manuallyCreateDealOpen = action.payload
    },
    setDocDrawModalOpen: (state, action: PayloadAction<boolean>) => {
      state.docDrawModalOpen = action.payload
    },
    setEscalationApprovalDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.escalationApprovalDrawerOpen = action.payload
    },
    setEscalationApprovalDrawerEscalation: (state, action: PayloadAction<Escalation>) => {
      state.escalationApprovalDrawerEscalation = action.payload
    },
    setAlertsTypes: (state, action: PayloadAction<string[]>) => {
      state.alertsTypes = action.payload
    },
    setCurrentStreamingNotePage: (state, action: PayloadAction<number>) => {
      state.currentStreamingNotePage = action.payload
    },
    setManualApplicationDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.manualApplicationDrawerOpen = action.payload
    },
    setManualApplicationDrawerStep: (state, action: PayloadAction<number>) => {
      state.manualApplicationDrawerStep = action.payload
    },
    setContactsCardsStatus: (
      state,
      action: PayloadAction<'custom' | 'allExpanded' | 'allCollapsed'>
    ) => {
      state.contactsCardsStatus = action.payload
    },
    setAddTradelineDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.addTradelineDrawerOpen = action.payload
    },
  },
})

export const {
  setPullCreditModalOpen,
  setPullCreditModalAction,
  setPullCreditModalUser,
  setViewTradelinesModalOpen,
  setTitleOrderModalOpen,
  setTitleOrderDrawerOpen,
  setAvmReportModalOpen,
  setAppraisalOrderModalOpen,
  setUsername,
  setDealAdvisorTab,
  setDealAdvisorOpen,
  setDealAdvisorSubTab,
  setFileContactsSubTab,
  setAddStreamingNoteValue,
  setAddStreamingNoteValueReply,
  hideSystemMessages,
  setContactsCardsStatus,
  setShowWorkviewsFilters,
  setManuallyCreateDealOpen,
  setCreateTradelinesModalOpen,
  setDocDrawModalOpen,
  setAddContactDrawerOpen,
  setClosedLossDrawerOpen,
  setClosedLossDrawerMode,
  setClosedLossDrawerReason,
  setClosedLossDrawerSubReasons,
  setClosedLossDrawerSubReasonOtherText,
  setClosedLossDrawerType,
  setUAMDeclineModalOpen,
  setUAMDeclineEscalation,
  setPullCreditDrawerStep,
  setAanReasons,
  setAvmDrawerStep,
  setEscalationApprovalDrawerOpen,
  setEscalationApprovalDrawerEscalation,
  setAddEscalationDrawerOpen,
  setAlertsTypes,
  setCurrentStreamingNotePage,
  setManualApplicationDrawerOpen,
  setManualApplicationDrawerStep,
  setAddTradelineDrawerOpen,
} = generalSlice.actions

export const generalReducer = generalSlice.reducer
